import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  $getSelection,
  $isParagraphNode,
  $isRangeSelection,
  $isTextNode,
  COMMAND_PRIORITY_LOW,
  FORMAT_TEXT_COMMAND,
  INDENT_CONTENT_COMMAND,
  OUTDENT_CONTENT_COMMAND,
  SELECTION_CHANGE_COMMAND,
} from 'lexical';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { $isLinkNode, TOGGLE_LINK_COMMAND } from '@lexical/link';
import { mergeRegister } from '@lexical/utils';
import { getSelectedNode } from '../../utils/getSelectedNode';
import {
  FormatBold,
  FormatItalic,
  FormatUnderlined,
  FormatStrikethrough,
  Subscript,
  Superscript,
  FormatSize,
  TextFields,
  Abc,
  Code as CodeIcon,
  Link as LinkIcon,
  FormatIndentIncrease,
  FormatIndentDecrease,
} from '@mui/icons-material';
import { Tooltip } from '@mui/material';

function TextFormatFloatingToolbar({
  editor,
  anchorElem,
  isBold,
  isItalic,
  isUnderline,
  isStrikethrough,
  setIsLinkEditMode,
}) {
  const popupCharStylesEditorRef = useRef(null);

  // const insertLink = useCallback(() => {
  //   if (!isLink) {
  //     setIsLinkEditMode(true);
  //     editor.dispatchCommand(TOGGLE_LINK_COMMAND, 'https://');
  //   } else {
  //     setIsLinkEditMode(false);
  //     editor.dispatchCommand(TOGGLE_LINK_COMMAND, null);
  //   }
  // }, [editor, isLink, setIsLinkEditMode]);

  // const transformSelectedText = useCallback(
  //   (transformer) => {
  //     editor.update(() => {
  //       const selection = $getSelection();
  //       if (!$isRangeSelection(selection) || selection.isCollapsed()) {
  //         return;
  //       }

  //       // extract() splits boundary text nodes, so only selected fragments are transformed.
  //       const selectedNodes = selection.extract();
  //       selectedNodes.forEach((node) => {
  //         if ($isTextNode(node)) {
  //           node.setTextContent(transformer(node.getTextContent()));
  //         }
  //       });
  //     });
  //   },
  //   [editor]
  // );

  function mouseMoveListener(e) {
    if (popupCharStylesEditorRef?.current && (e.buttons === 1 || e.buttons === 3)) {
      if (popupCharStylesEditorRef.current.style.pointerEvents !== 'none') {
        const x = e.clientX;
        const y = e.clientY;
        const elementUnderMouse = document.elementFromPoint(x, y);
        if (!popupCharStylesEditorRef.current.contains(elementUnderMouse)) {
          popupCharStylesEditorRef.current.style.pointerEvents = 'none';
        }
      }
    }
  }

  function mouseUpListener() {
    if (popupCharStylesEditorRef?.current) {
      if (popupCharStylesEditorRef.current.style.pointerEvents !== 'auto') {
        popupCharStylesEditorRef.current.style.pointerEvents = 'auto';
      }
    }
  }

  useEffect(() => {
    if (popupCharStylesEditorRef?.current) {
      document.addEventListener('mousemove', mouseMoveListener);
      document.addEventListener('mouseup', mouseUpListener);
      return () => {
        document.removeEventListener('mousemove', mouseMoveListener);
        document.removeEventListener('mouseup', mouseUpListener);
      };
    }
  }, []);

  const $updateTextFormatFloatingToolbar = useCallback(() => {
    const selection = $getSelection();
    const popupCharStylesEditorElem = popupCharStylesEditorRef.current;
    const nativeSelection = window.getSelection();

    if (popupCharStylesEditorElem === null) return;

    const rootElement = editor.getRootElement();
    if (
      selection !== null &&
      nativeSelection !== null &&
      !nativeSelection.isCollapsed &&
      rootElement !== null &&
      rootElement.contains(nativeSelection.anchorNode)
    ) {
      const rangeRect = getDOMRangeRect(nativeSelection, rootElement);
      setFloatingElemPosition(rangeRect, popupCharStylesEditorElem, anchorElem);
    }
  }, [editor, anchorElem]);

  useEffect(() => {
    const scrollerElem = anchorElem.parentElement;

    const update = () => {
      editor.getEditorState().read(() => {
        $updateTextFormatFloatingToolbar();
      });
    };

    window.addEventListener('resize', update);
    if (scrollerElem) {
      scrollerElem.addEventListener('scroll', update);
    }

    return () => {
      window.removeEventListener('resize', update);
      if (scrollerElem) {
        scrollerElem.removeEventListener('scroll', update);
      }
    };
  }, [editor, $updateTextFormatFloatingToolbar, anchorElem]);

  useEffect(() => {
    editor.getEditorState().read(() => {
      $updateTextFormatFloatingToolbar();
    });
    return mergeRegister(
      editor.registerUpdateListener(({ editorState }) => {
        editorState.read(() => {
          $updateTextFormatFloatingToolbar();
        });
      }),
      editor.registerCommand(
        SELECTION_CHANGE_COMMAND,
        () => {
          $updateTextFormatFloatingToolbar();
          return false;
        },
        COMMAND_PRIORITY_LOW
      )
    );
  }, [editor, $updateTextFormatFloatingToolbar]);

  return (
    <div ref={popupCharStylesEditorRef} className="floating-text-format-popup">
      <Tooltip title="Bold" arrow placement="top">
        <button
          type="button"
          onClick={() => editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'bold')}
          className={'popup-item spaced ' + (isBold ? 'active' : '')}
          aria-label="Format text as bold"
        >
          <FormatBold fontSize="small" />
        </button>
      </Tooltip>
      <Tooltip title="Italic" arrow placement="top">
        <button
          type="button"
          onClick={() => editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'italic')}
          className={'popup-item spaced ' + (isItalic ? 'active' : '')}
          aria-label="Format text as italics"
        >
          <FormatItalic fontSize="small" />
        </button>
      </Tooltip>
      <Tooltip title="Underline" arrow placement="top">
        <button
          type="button"
          onClick={() => editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'underline')}
          className={'popup-item spaced ' + (isUnderline ? 'active' : '')}
          aria-label="Format text to underlined"
        >
          <FormatUnderlined fontSize="small" />
        </button>
      </Tooltip>
      <Tooltip title="Strikethrough" arrow placement="top">
        <button
          type="button"
          onClick={() => editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'strikethrough')}
          className={'popup-item spaced ' + (isStrikethrough ? 'active' : '')}
          aria-label="Format text with a strikethrough"
        >
          <FormatStrikethrough fontSize="small" />
        </button>
      </Tooltip>
      {/* Commented out: Subscript, Superscript, Uppercase, Lowercase, Capitalize, Code, Link */}
      {/* <Tooltip title="Subscript" arrow placement="top">
        <button
          type="button"
          onClick={() => editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'subscript')}
          className={'popup-item spaced ' + (isSubscript ? 'active' : '')}
          aria-label="Format subscript"
        >
          <Subscript fontSize="small" />
        </button>
      </Tooltip>
      <Tooltip title="Superscript" arrow placement="top">
        <button
          type="button"
          onClick={() => editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'superscript')}
          className={'popup-item spaced ' + (isSuperscript ? 'active' : '')}
          aria-label="Format superscript"
        >
          <Superscript fontSize="small" />
        </button>
      </Tooltip>
      <Tooltip title="Uppercase" arrow placement="top">
        <button
          type="button"
          onClick={() => transformSelectedText((text) => text.toUpperCase())}
          className="popup-item spaced"
          aria-label="Transform text to uppercase"
        >
          <FormatSize fontSize="small" />
        </button>
      </Tooltip>
      <Tooltip title="Lowercase" arrow placement="top">
        <button
          type="button"
          onClick={() => transformSelectedText((text) => text.toLowerCase())}
          className="popup-item spaced"
          aria-label="Transform text to lowercase"
        >
          <TextFields fontSize="small" />
        </button>
      </Tooltip>
      <Tooltip title="Capitalize" arrow placement="top">
        <button
          type="button"
          onClick={() => transformSelectedText((text) => text.replace(/\b\w/g, (c) => c.toUpperCase()))}
          className="popup-item spaced"
          aria-label="Capitalize each word"
        >
          <Abc fontSize="small" />
        </button>
      </Tooltip>
      <Tooltip title="Code" arrow placement="top">
        <button
          type="button"
          onClick={() => editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'code')}
          className={'popup-item spaced ' + (isCode ? 'active' : '')}
          aria-label="Insert code block"
        >
          <CodeIcon fontSize="small" />
        </button>
      </Tooltip>
      <Tooltip title="Link" arrow placement="top">
        <button
          type="button"
          onClick={insertLink}
          className={'popup-item spaced ' + (isLink ? 'active' : '')}
          aria-label="Insert link"
        >
          <LinkIcon fontSize="small" />
        </button>
      </Tooltip> */}
      <Tooltip title="Indent" arrow placement="top">
        <button
          type="button"
          onClick={() => editor.dispatchCommand(INDENT_CONTENT_COMMAND, undefined)}
          className="popup-item spaced"
          aria-label="Indent content"
        >
          <FormatIndentIncrease fontSize="small" />
        </button>
      </Tooltip>
      <Tooltip title="Outdent" arrow placement="top">
        <button
          type="button"
          onClick={() => editor.dispatchCommand(OUTDENT_CONTENT_COMMAND, undefined)}
          className="popup-item spaced"
          aria-label="Outdent content"
        >
          <FormatIndentDecrease fontSize="small" />
        </button>
      </Tooltip>
    </div>
  );
}

function getDOMRangeRect(nativeSelection, rootElement) {
  const domRange = nativeSelection.getRangeAt(0);
  let rect;
  if (nativeSelection.anchorNode === rootElement) {
    let inner = rootElement;
    while (inner.firstElementChild != null) {
      inner = inner.firstElementChild;
    }
    rect = inner.getBoundingClientRect();
  } else {
    rect = domRange.getBoundingClientRect();
  }
  return rect;
}

function setFloatingElemPosition(targetRect, floatingElem, anchorElem) {
  const scrollerElem = anchorElem.parentElement;
  if (targetRect === null || !scrollerElem) {
    floatingElem.style.opacity = '0';
    floatingElem.style.transform = 'translate(-10000px, -10000px)';
    return;
  }

  const floatingElemRect = floatingElem.getBoundingClientRect();
  const anchorElementRect = anchorElem.getBoundingClientRect();
  const editorScrollerRect = scrollerElem.getBoundingClientRect();

  let top = targetRect.top - floatingElemRect.height - 8 - anchorElementRect.top;
  let left = targetRect.left - anchorElementRect.left + (targetRect.width - floatingElemRect.width) / 2;
  const anchorPopupSide = anchorElem.getAttribute('data-anchor-popup-side');
  if (top < editorScrollerRect.top - anchorElementRect.top + 8 || anchorPopupSide === 'above') {
    top = targetRect.top - anchorElementRect.top + targetRect.height + 8;
  }

  if (left < 0) left = 4;

  floatingElem.style.opacity = '1';
  floatingElem.style.transform = `translate(${left}px, ${top}px)`;
}

function useFloatingTextFormatToolbar(editor, anchorElem, setIsLinkEditMode) {
  const [isText, setIsText] = useState(false);
  // const [isLink, setIsLink] = useState(false);
  const [isBold, setIsBold] = useState(false);
  const [isItalic, setIsItalic] = useState(false);
  const [isUnderline, setIsUnderline] = useState(false);
  const [isStrikethrough, setIsStrikethrough] = useState(false);
  // const [isSubscript, setIsSubscript] = useState(false);
  // const [isSuperscript, setIsSuperscript] = useState(false);
  // const [isCode, setIsCode] = useState(false);

  const updatePopup = useCallback(() => {
    editor.getEditorState().read(() => {
      // Should not pop up the floating toolbar when using IME input
      if (editor.isComposing()) {
        return;
      }
      const selection = $getSelection();
      const nativeSelection = window.getSelection();
      const rootElement = editor.getRootElement();

      if (
        nativeSelection !== null &&
        (!$isRangeSelection(selection) ||
          rootElement === null ||
          !rootElement.contains(nativeSelection.anchorNode))
      ) {
        setIsText(false);
        return;
      }

      if (!$isRangeSelection(selection)) {
        return;
      }

      const node = getSelectedNode(selection);

      setIsBold(selection.hasFormat('bold'));
      setIsItalic(selection.hasFormat('italic'));
      setIsUnderline(selection.hasFormat('underline'));
      setIsStrikethrough(selection.hasFormat('strikethrough'));
      // setIsSubscript(selection.hasFormat('subscript'));
      // setIsSuperscript(selection.hasFormat('superscript'));
      // setIsCode(selection.hasFormat('code'));

      // const parent = node.getParent();
      // if ($isLinkNode(parent) || $isLinkNode(node)) {
      //   setIsLink(true);
      // } else {
      //   setIsLink(false);
      // }

      if (
        !$isCodeNode(node.getParent()) &&
        selection.getTextContent() !== ''
      ) {
        setIsText($isTextNode(node) || $isParagraphNode(node));
      } else {
        setIsText(false);
      }

      const rawTextContent = selection.getTextContent().replace(/\n/g, '');
      if (!selection.isCollapsed() && rawTextContent === '') {
        setIsText(false);
        return;
      }
    });
  }, [editor]);

  useEffect(() => {
    document.addEventListener('selectionchange', updatePopup);
    return () => {
      document.removeEventListener('selectionchange', updatePopup);
    };
  }, [updatePopup]);

  useEffect(() => {
    return mergeRegister(
      editor.registerUpdateListener(() => {
        updatePopup();
      }),
      editor.registerRootListener(() => {
        if (editor.getRootElement() === null) {
          setIsText(false);
        }
      })
    );
  }, [editor, updatePopup]);

  if (!isText) {
    return null;
  }

  return createPortal(
    <TextFormatFloatingToolbar
      editor={editor}
      anchorElem={anchorElem}
      isBold={isBold}
      isItalic={isItalic}
      isUnderline={isUnderline}
      isStrikethrough={isStrikethrough}
      setIsLinkEditMode={setIsLinkEditMode}
    />,
    anchorElem
  );
}

function $isCodeNode(node) {
  return node?.getType?.() === 'code';
}

export default function FloatingTextFormatToolbarPlugin({ anchorElem = document.body, setIsLinkEditMode }) {
  const [editor] = useLexicalComposerContext();
  return useFloatingTextFormatToolbar(editor, anchorElem, setIsLinkEditMode);
}
